import React, { useState } from 'react';
import { 
  Card, 
  List, 
  Tag, 
  Progress, 
  Button, 
  Space, 
  Modal, 
  Form, 
  InputNumber,
  Select,
  Typography,
  Divider,
  Empty
} from 'antd';
import { 
  CheckSquareOutlined, 
  ClockCircleOutlined, 
  EditOutlined,
  // PlayCircleOutlined, // 已移除“开始任务”按钮
  CheckCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { message } from 'antd';

const { Option } = Select;
const { Text, Title } = Typography;

const MyTasks = () => {
  const queryClient = useQueryClient();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [form] = Form.useForm();
  
  // 获取我的任务
  const { data: myTasksData } = useQuery(
    'myTasks',
    () => axios.get('/tasks/my').then(res => res.data.data),
    { refetchInterval: 30000 }
  );

  // 更新任务（函数级注释：用于更新当前选择任务的状态与进度，提交后刷新“我的任务”列表）
  const updateTaskMutation = useMutation(
    ({ taskId, updateData }) => axios.put(`/tasks/${taskId}`, updateData),
    {
      onSuccess: () => {
        message.success('任务更新成功');
        setIsModalVisible(false);
        setSelectedTask(null);
        form.resetFields();
        queryClient.invalidateQueries('myTasks');
        queryClient.invalidateQueries('projects');
        queryClient.invalidateQueries('recentProjects');
        if (selectedTask?.projectId) {
          queryClient.invalidateQueries(['project', selectedTask.projectId]);
        }
      },
      onError: (error) => {
        message.error(error.response?.data?.message || '更新失败');
      }
    }
  );

  // 函数级注释：打开“更新进度”模态框，并回填任务当前进度和状态
  const handleUpdateProgress = (task) => {
    setSelectedTask(task);
    form.setFieldsValue({
      progress: task.progress,
      status: task.status
    });
    setIsModalVisible(true);
  };



  // 函数级注释：确认后将任务标记为“已完成”，并设置进度为100%
  const handleCompleteTask = (task) => {
    Modal.confirm({
      title: '确认完成任务',
      content: `确定要将任务"${task.title}"标记为已完成吗？`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        updateTaskMutation.mutate({
          taskId: task.id,
          updateData: { 
            status: 'completed',
            progress: 100
          }
        });
      }
    });
  };

  // 函数级注释：提交“更新进度”表单
  const handleModalOk = () => {
    form.validateFields().then(values => {
      updateTaskMutation.mutate({
        taskId: selectedTask.id,
        updateData: values
      });
    });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'red',
      medium: 'orange',
      low: 'green'
    };
    return colors[priority] || 'default';
  };

  const getPriorityText = (priority) => {
    const texts = {
      high: '高优先级',
      medium: '中优先级',
      low: '低优先级'
    };
    return texts[priority] || priority;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'default',
      in_progress: 'processing',
      completed: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: '待开始',
      in_progress: '进行中',
      completed: '已完成',
      cancelled: '已取消'
    };
    return texts[status] || status;
  };

  const isOverdue = (dueDate, status) => {
    return status !== 'completed' && dueDate && dayjs().isAfter(dayjs(dueDate));
  };

  // 函数级注释：渲染任务操作区（根据最新需求，已取消“开始任务”按钮与相关功能）
  const renderTaskActions = (task) => {
    const actions = [];
    

    
    if (['pending', 'in_progress'].includes(task.status)) {
      actions.push(
        <Button 
          key="update"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleUpdateProgress(task)}
        >
          更新进度
        </Button>
      );
    }
    
    if (task.status === 'in_progress' && task.progress >= 90) {
      actions.push(
        <Button 
          key="complete"
          type="primary" 
          size="small"
          icon={<CheckCircleOutlined />}
          onClick={() => handleCompleteTask(task)}
        >
          标记完成
        </Button>
      );
    }

    return actions;
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <CheckSquareOutlined />
            我的任务
          </Space>
        }
        className="status-card"
      >
        {!myTasksData?.tasksByProject || myTasksData.tasksByProject.length === 0 ? (
          <Empty 
            description="暂无任务"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          myTasksData.tasksByProject.map((projectGroup, index) => (
            <div key={projectGroup.project.id}>
              {index > 0 && <Divider />}
              
              <Title level={4} style={{ marginBottom: '16px' }}>
                {projectGroup.project.name}
                <Tag style={{ marginLeft: '8px' }}>
                  {projectGroup.tasks.length} 个任务
                </Tag>
              </Title>

              <List
                dataSource={projectGroup.tasks}
                renderItem={(task) => (
                  <List.Item
                    style={{ 
                      padding: '16px 0',
                      borderBottom: '1px solid #f0f0f0'
                    }}
                    actions={renderTaskActions(task)}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>{task.title}</Text>
                          <Tag color={getPriorityColor(task.priority)}>
                            {getPriorityText(task.priority)}
                          </Tag>
                          <Tag color={getStatusColor(task.status)}>
                            {getStatusText(task.status)}
                          </Tag>
                          {isOverdue(task.dueDate, task.status) && (
                            <Tag color="red">已逾期</Tag>
                          )}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {task.description && (
                            <Text type="secondary">{task.description}</Text>
                          )}
                          
                          <Space>
                            {task.dueDate && (
                              <>
                                <ClockCircleOutlined />
                                <Text type="secondary">
                                  截止时间: {dayjs(task.dueDate).format('YYYY-MM-DD HH:mm')}
                                </Text>
                              </>
                            )}
                            {task.estimatedHours && (
                              <Text type="secondary">
                                预估: {task.estimatedHours}h
                              </Text>
                            )}
                            {task.actualHours > 0 && (
                              <Text type="secondary">
                                实际: {task.actualHours}h
                              </Text>
                            )}
                          </Space>

                          <div style={{ marginTop: '8px' }}>
                            <Progress 
                              percent={task.progress} 
                              size="small"
                              status={task.progress === 100 ? 'success' : 'active'}
                            />
                          </div>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          ))
        )}
      </Card>

      {/* 更新任务进度模态框 */}
      <Modal
        title="更新任务进度"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedTask(null);
          form.resetFields();
        }}
        confirmLoading={updateTaskMutation.isLoading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="status"
            label="任务状态"
            rules={[{ required: true, message: '请选择任务状态' }]}
          >
            <Select>
              <Option value="pending">待开始</Option>
              <Option value="in_progress">进行中</Option>
              <Option value="completed">已完成</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="progress"
            label="完成进度 (%)"
            rules={[{ required: true, message: '请输入完成进度' }]}
          >
            <InputNumber
              min={0}
              max={100}
              style={{ width: '100%' }}
              formatter={value => `${value}%`}
              parser={value => value.replace('%', '')}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 说明：根据需求移除“加班申请模态框”及其相关状态、函数与UI */}
    </div>
  );
};

export default MyTasks;
